import React, { useMemo, useState } from 'react';
import { FiCopy, FiX } from 'react-icons/fi';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import { CodeSyntaxPreview } from './CodeSyntaxPreview.tsx';
import { SqlSyntaxPreview } from './SqlSyntaxPreview.tsx';
import {
  SCIPLEX_CODE_CUSTOM_THEME,
  SCIPLEX_CODE_THEMES,
  SCIPLEX_SQL_CUSTOM_THEME,
  SCIPLEX_SQL_THEMES,
  CODE_THEME_TOKEN_FIELDS,
  SQL_THEME_TOKEN_FIELDS,
  areCodeCustomThemeTokensEqual,
  areSqlCustomThemeTokensEqual,
  getCodeThemePreviewStyle,
  getCodeThemeTokenValues,
  getSqlThemePreviewStyle,
  getThemeTokenValues,
  isCustomCodeTheme,
  isCustomSqlTheme,
  normalizeHexColor,
  type CodeCustomThemeTokens,
  type CodeThemeTokenFieldKey,
  type EditorBackgroundMode,
  type SciplexCodeThemeId,
  type SciplexSqlThemeId,
  type SqlCustomThemeTokens,
  type SqlThemeTokenFieldKey,
} from '../utils/monacoThemes.ts';
import './css/EditorSettingsModal.css';

const SQL_THEME_PREVIEW_CODE = `-- active users
SELECT
  status,
  COUNT(*) AS total,
  MAX(age) AS max_age
FROM users
WHERE status = 'active'
GROUP BY status
HAVING MAX(age) > 18;`;

const CODE_THEME_PREVIEW_CODE = `def active_users(users):
    # filter active records
    total = len(users)
    return max(u["age"] for u in users if u["status"] == "active")`;

export type EditorSettingsModalProps = {
  isSql: boolean;
  syntaxTheme: SciplexSqlThemeId | SciplexCodeThemeId;
  customThemeTokens: SqlCustomThemeTokens | CodeCustomThemeTokens;
  editorBackgroundMode: EditorBackgroundMode;
  onSyntaxThemeChange: (
    theme: SciplexSqlThemeId | SciplexCodeThemeId,
    customTokens?: SqlCustomThemeTokens | CodeCustomThemeTokens,
    backgroundMode?: EditorBackgroundMode,
  ) => void;
  onResetCode: () => void;
  canResetCode: boolean;
  t: (key: string) => string;
  onClose: () => void;
};

export function EditorSettingsModal({
  isSql,
  syntaxTheme,
  customThemeTokens,
  editorBackgroundMode,
  onSyntaxThemeChange,
  onResetCode,
  canResetCode,
  t,
  onClose,
}: EditorSettingsModalProps) {
  useCloseOnEscape(true, onClose);
  const [draftTheme, setDraftTheme] = useState(syntaxTheme);
  const [draftCustomTokens, setDraftCustomTokens] = useState(customThemeTokens);
  const [draftBackgroundMode, setDraftBackgroundMode] = useState(editorBackgroundMode);
  const [draftTokenInputs, setDraftTokenInputs] = useState<
    Partial<Record<SqlThemeTokenFieldKey | CodeThemeTokenFieldKey, string>>
  >({});
  const [copiedTokenKey, setCopiedTokenKey] = useState<string | null>(null);

  const themeOptions = isSql ? SCIPLEX_SQL_THEMES : SCIPLEX_CODE_THEMES;
  const tokenFields = isSql ? SQL_THEME_TOKEN_FIELDS : CODE_THEME_TOKEN_FIELDS;
  const isDraftCustom = isSql
    ? isCustomSqlTheme(draftTheme as SciplexSqlThemeId)
    : isCustomCodeTheme(draftTheme as SciplexCodeThemeId);

  const displayedTokenValues = useMemo(() => {
    if (isSql) {
      return isDraftCustom
        ? (draftCustomTokens as SqlCustomThemeTokens)
        : getThemeTokenValues(draftTheme as SciplexSqlThemeId);
    }
    return isDraftCustom
      ? (draftCustomTokens as CodeCustomThemeTokens)
      : getCodeThemeTokenValues(draftTheme as SciplexCodeThemeId);
  }, [draftCustomTokens, draftTheme, isDraftCustom, isSql]);

  const previewStyle = useMemo(() => {
    if (isSql) {
      return isDraftCustom
        ? getSqlThemePreviewStyle(
            SCIPLEX_SQL_CUSTOM_THEME,
            draftCustomTokens as SqlCustomThemeTokens,
            draftBackgroundMode,
          )
        : getSqlThemePreviewStyle(draftTheme as SciplexSqlThemeId, undefined, draftBackgroundMode);
    }
    return isDraftCustom
      ? getCodeThemePreviewStyle(
          SCIPLEX_CODE_CUSTOM_THEME,
          draftCustomTokens as CodeCustomThemeTokens,
          draftBackgroundMode,
        )
      : getCodeThemePreviewStyle(draftTheme as SciplexCodeThemeId, undefined, draftBackgroundMode);
  }, [draftBackgroundMode, draftCustomTokens, draftTheme, isDraftCustom, isSql]);

  const hasPendingThemeChange = useMemo(() => {
    if (draftTheme !== syntaxTheme) return true;
    if (draftBackgroundMode !== editorBackgroundMode) return true;
    if (!isDraftCustom) return false;
    if (isSql) {
      return !areSqlCustomThemeTokensEqual(
        draftCustomTokens as SqlCustomThemeTokens,
        customThemeTokens as SqlCustomThemeTokens,
      );
    }
    return !areCodeCustomThemeTokensEqual(
      draftCustomTokens as CodeCustomThemeTokens,
      customThemeTokens as CodeCustomThemeTokens,
    );
  }, [
    customThemeTokens,
    draftBackgroundMode,
    draftCustomTokens,
    draftTheme,
    editorBackgroundMode,
    isDraftCustom,
    isSql,
    syntaxTheme,
  ]);

  const handleApplyTheme = () => {
    if (!hasPendingThemeChange) return;
    onSyntaxThemeChange(draftTheme, draftCustomTokens, draftBackgroundMode);
  };

  const handleTokenChange = (key: SqlThemeTokenFieldKey | CodeThemeTokenFieldKey, rawValue: string) => {
    setDraftTokenInputs((prev) => ({ ...prev, [key]: rawValue }));
    const normalized = normalizeHexColor(rawValue);
    if (normalized) {
      setDraftCustomTokens((prev) => ({ ...prev, [key]: normalized }));
    }
  };

  const handleTokenBlur = (key: SqlThemeTokenFieldKey | CodeThemeTokenFieldKey) => {
    const rawValue = draftTokenInputs[key];
    if (rawValue == null) return;

    const normalized =
      normalizeHexColor(rawValue) ?? (draftCustomTokens as Record<string, string>)[key];
    setDraftCustomTokens((prev) => ({ ...prev, [key]: normalized }));
    setDraftTokenInputs((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const getCustomTokenInputValue = (key: SqlThemeTokenFieldKey | CodeThemeTokenFieldKey) =>
    draftTokenInputs[key] ?? (draftCustomTokens as Record<string, string>)[key];

  const handleCopyColor = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedTokenKey(key);
      window.setTimeout(() => setCopiedTokenKey((current) => (current === key ? null : current)), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="editor-settings-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="editor-settings-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="editor-settings-modal-title"
      >
        <header className="editor-settings-modal__header">
          <h3 id="editor-settings-modal-title">{t('problemPage.editorSettings')}</h3>
          <button
            type="button"
            className="editor-settings-modal__close"
            onClick={onClose}
            aria-label={t('problemPage.close')}
          >
            <FiX strokeWidth={1.75} aria-hidden />
          </button>
        </header>

        <div className="editor-settings-modal__content">
          <section className="editor-settings-modal__section">
            <div className="editor-settings-modal__theme-layout">
              <div className="editor-settings-modal__theme-controls">
                <label className="editor-settings-modal__label" htmlFor="syntax-theme-select">
                  {t('problemPage.syntaxThemeLabel')}
                </label>
                <select
                  id="syntax-theme-select"
                  className="editor-settings-modal__select"
                  value={draftTheme}
                  onChange={(e) =>
                    setDraftTheme(e.target.value as SciplexSqlThemeId | SciplexCodeThemeId)
                  }
                  aria-label={t('problemPage.syntaxThemeLabel')}
                >
                  {themeOptions.map((theme) => (
                    <option key={theme.id} value={theme.id}>
                      {t(theme.labelKey)}
                    </option>
                  ))}
                </select>

                <label className="editor-settings-modal__label" htmlFor="editor-background-mode-select">
                  {t('problemPage.editorBackgroundLabel')}
                </label>
                <select
                  id="editor-background-mode-select"
                  className="editor-settings-modal__select"
                  value={draftBackgroundMode}
                  onChange={(e) => setDraftBackgroundMode(e.target.value as EditorBackgroundMode)}
                  aria-label={t('problemPage.editorBackgroundLabel')}
                >
                  <option value="sciplex">{t('problemPage.editorBackgroundSciplex')}</option>
                  <option value="ide">{t('problemPage.editorBackgroundIde')}</option>
                </select>
              </div>

              <div
                className="editor-settings-modal__preview"
                style={{ backgroundColor: previewStyle.background }}
                aria-label={t('problemPage.syntaxThemePreview')}
              >
                {isSql ? (
                  <SqlSyntaxPreview
                    code={SQL_THEME_PREVIEW_CODE}
                    style={previewStyle as ReturnType<typeof getSqlThemePreviewStyle>}
                  />
                ) : (
                  <CodeSyntaxPreview
                    code={CODE_THEME_PREVIEW_CODE}
                    style={previewStyle as ReturnType<typeof getCodeThemePreviewStyle>}
                  />
                )}
              </div>
            </div>
          </section>

          <section className="editor-settings-modal__section editor-settings-modal__section--tokens">
            <p className="editor-settings-modal__label">{t('problemPage.syntaxThemeTokensTitle')}</p>
            <p className="editor-settings-modal__hint">
              {isDraftCustom
                ? t('problemPage.syntaxThemeTokensCustomHint')
                : t('problemPage.syntaxThemeTokensPresetHint')}
            </p>

            <div className="editor-settings-modal__token-grid">
              {tokenFields.map(({ key, labelKey }) => {
                const value = displayedTokenValues[key as keyof typeof displayedTokenValues] as string;
                const inputId = `syntax-theme-token-${key}`;

                return (
                  <div
                    key={key}
                    className={`editor-settings-modal__token-row${
                      isDraftCustom
                        ? ' editor-settings-modal__token-row--custom'
                        : ' editor-settings-modal__token-row--preset'
                    }`}
                  >
                    <label className="editor-settings-modal__token-label" htmlFor={inputId}>
                      {t(labelKey)}
                    </label>
                    <span
                      className="editor-settings-modal__token-swatch"
                      style={{ backgroundColor: value }}
                      aria-hidden
                    />
                    {isDraftCustom ? (
                      <input
                        id={inputId}
                        type="text"
                        className="editor-settings-modal__token-input"
                        value={getCustomTokenInputValue(key)}
                        onChange={(e) => handleTokenChange(key, e.target.value)}
                        onBlur={() => handleTokenBlur(key)}
                        spellCheck={false}
                        inputMode="text"
                        autoComplete="off"
                        aria-label={t(labelKey)}
                      />
                    ) : (
                      <>
                        <input
                          id={inputId}
                          type="text"
                          className="editor-settings-modal__token-input editor-settings-modal__token-input--readonly"
                          value={value}
                          readOnly
                        />
                        <button
                          type="button"
                          className="editor-settings-modal__token-copy"
                          onClick={() => void handleCopyColor(key, value)}
                          aria-label={t('problemPage.syntaxThemeCopyColor')}
                          title={
                            copiedTokenKey === key
                              ? t('problemPage.syntaxThemeColorCopied')
                              : t('problemPage.syntaxThemeCopyColor')
                          }
                        >
                          <FiCopy strokeWidth={1.75} aria-hidden />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="editor-settings-modal__section editor-settings-modal__section--apply">
            <button
              type="button"
              className="editor-settings-modal__apply-btn"
              onClick={handleApplyTheme}
              disabled={!hasPendingThemeChange}
            >
              {t('problemPage.syntaxThemeApply')}
            </button>
          </section>

          <section className="editor-settings-modal__section editor-settings-modal__section--actions">
            <button
              type="button"
              className="editor-settings-modal__reset-btn"
              onClick={() => {
                onResetCode();
                onClose();
              }}
              disabled={!canResetCode}
            >
              {t('problemPage.resetCode')}
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
